import React, { useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, Box, Typography, IconButton, Button, TextField, Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { FaSearch } from "react-icons/fa";
import api from "../Api";
import { openPdfWindow } from "../utils/pdfWindow";

// Search the server pdf-archive by tracking id / order id, merge the matching
// label pages, and open the combined PDF.
export default function LabelSearchDialog({ open, onClose }) {
  const [raw, setRaw] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const search = async () => {
    const query = raw.trim();
    if (!query) { setMsg({ t: "error", m: "Enter a tracking id or order id." }); return; }
    // Claim the tab synchronously (inside the click) so the popup blocker on
    // other PCs doesn't silently block the result after the fetch.
    const win = openPdfWindow();
    setBusy(true); setMsg(null);
    try {
      const resp = await api.post("/api/label-search", { query }, { responseType: "blob" });
      const found = resp.headers["x-labels-found"];
      const opened = win.show(new Blob([resp.data], { type: "application/pdf" }), "labels.pdf");
      setMsg({
        t: "success",
        m: opened
          ? `Opened labels${found ? ` (${found} found)` : ""}.`
          : `Popup blocked by the browser — downloaded the labels PDF instead${found ? ` (${found} found)` : ""}.`,
      });
    } catch (err) {
      win.fail();
      let m = "No labels found on the server for those id(s).";
      try { m = JSON.parse(await err.response.data.text()).error || m; } catch (_) {}
      setMsg({ t: err.response?.status === 404 ? "warning" : "error", m });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", fontWeight: 800 }}>
        🔎 Label Search (server)
        <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Enter tracking ID(s) or order ID(s) — one per line or comma separated. Searches the
          uploaded Myntra &amp; Flipkart print archive, recovers missing Flipkart labels and Myntra label+invoice pairs from the live server,
          merges the matching labels, and opens them.
        </Typography>
        <TextField
          fullWidth multiline minRows={4} autoFocus
          label="Tracking / Order IDs"
          placeholder={"MYSC1320450415\nOD438065065502579100"}
          value={raw}
          onChange={(e) => setRaw(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) search(); }}
        />
        {msg && <Alert severity={msg.t} sx={{ mt: 2 }}>{msg.m}</Alert>}
        <Box sx={{ mt: 2 }}>
          <Button variant="contained" startIcon={<FaSearch />} disabled={busy || !raw.trim()} onClick={search}>
            {busy ? "Searching…" : "Search & Open"}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
